import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ChevronDown, Clock, Loader2, Plus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { PACK, PACK_TZ, packBody, timeLabel } from "@/lib/signalPack";
import { cn } from "@/lib/utils";

const body = packBody;

const Item = ({ w, added, busy, onAdd }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-[#27272A] bg-[#0f0f12]" data-testid={`ssp-item-${w.key}`}>
      <div className="flex flex-wrap items-center gap-2 p-3">
        <button onClick={() => setOpen(!open)} className="min-w-0 flex-1 basis-[calc(100%-7rem)] text-left sm:basis-auto" data-testid={`ssp-toggle-${w.key}`}>
          <p className="line-clamp-2 text-sm font-semibold text-zinc-100 sm:truncate">{w.name}</p>
          <p className="mt-0.5 flex items-center gap-1 text-[11px] text-zinc-500"><Clock className="h-3 w-3" />Daily {timeLabel(w.time)} · {w.kind.replace(/_/g, " ")}</p>
        </button>
        {added ? <span className="flex items-center gap-1 text-[11px] text-emerald-300" data-testid={`ssp-added-${w.key}`}><CheckCircle2 className="h-3.5 w-3.5" />Added</span>
          : <Button size="sm" variant="outline" onClick={() => onAdd([w])} disabled={busy} className="h-7 border-[#D4AF37]/50 text-[#F3C649]" data-testid={`ssp-add-${w.key}`}><Plus className="h-3.5 w-3.5" />Add</Button>}
        <button onClick={() => setOpen(!open)} className="text-zinc-500 hover:text-zinc-200" aria-label="Show details" data-testid={`ssp-chevron-${w.key}`}><ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} /></button>
      </div>
      {open && <div className="space-y-2 border-t border-[#1f1f23] p-3 text-xs" data-testid={`ssp-detail-${w.key}`}>
        <p className="text-zinc-200">{w.what}</p>
        <p className="text-zinc-400"><span className="text-[#F3C649]">Why it sells: </span>{w.why}</p>
        <div><p className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">Signals</p><div className="flex flex-wrap gap-1">{w.signals.map((s) => <span key={s} className="rounded-full border border-[#8224e3]/40 px-2 py-0.5 text-[10px] text-[#d7b6ff]">{s}</span>)}</div></div>
        <div><p className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">Sources</p><p className="text-[11px] text-zinc-400">{w.sources.join(" · ")}</p></div>
      </div>}
    </div>
  );
};

export const SignalPack = ({ onInstalled }) => {
  const [have, setHave] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);
  const load = () => api.get("/admin/rcc/workflows").then((r) => setHave(new Set((r.data?.items || []).map((w) => w.name)))).catch(() => setHave(new Set()));
  useEffect(() => { load(); }, []);
  const missing = PACK.filter((w) => !have?.has(w.name));
  const add = async (list) => {
    setBusy(true);
    const res = await Promise.all(list.map((w) => api.post("/admin/rcc/workflows", body(w)).then(() => null).catch((e) => `${w.name}: ${errMsg(e)}`)));
    const bad = res.filter(Boolean);
    const ok = list.length - bad.length;
    if (ok) {
      await api.put("/admin/rcc/radar/auto-add", { enabled: true, hour: 3, timezone: PACK_TZ, run_radar: true, email_summary: true }).catch(() => null);
      toast.success(`${ok} signal workflow${ok === 1 ? "" : "s"} added. They run daily from 12:00am MST, and new companies are added to Opportunity Radar automatically at 3am.`, { duration: 8000 });
    }
    if (bad.length) toast.error(`Couldn't add ${bad.length}: ${bad[0]}`);
    await load(); onInstalled?.(); setBusy(false);
  };
  if (!have) return null;
  return (
    <div className="rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/[0.07] to-transparent p-4" data-testid="ssp">
      <div className="flex flex-wrap items-start gap-3">
        <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" />
        <div className="min-w-0 flex-1 basis-[calc(100%-2.5rem)] sm:basis-auto">
          <p className="font-display text-lg text-zinc-50">Sales Signal Pack</p>
          <p className="text-xs text-zinc-400" data-testid="ssp-summary">10 daily workflows that find US startups and companies with 1-500 people who are likely to buy recruiting help right now. They run one after another from 12:00am to 2:15am MST. Companies with 2 or more signals are marked Hot. {PACK.length - missing.length} of {PACK.length} added.</p>
        </div>
        <div className="ml-8 flex items-center gap-2 sm:ml-0">
          {missing.length > 0 ? <Button size="sm" onClick={() => add(missing)} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="ssp-add-all">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Add {missing.length === PACK.length ? "all 10" : `the other ${missing.length}`}</Button>
            : <span className="flex items-center gap-1 text-xs text-emerald-300" data-testid="ssp-all-added"><CheckCircle2 className="h-4 w-4" />All 10 added</span>}
          <button onClick={() => setOpen(!open)} className="text-zinc-500 hover:text-zinc-200" data-testid="ssp-collapse"><ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} /></button>
        </div>
      </div>
      {open && <div className="mt-3 space-y-2" data-testid="ssp-list">{PACK.map((w) => <Item key={w.key} w={w} added={have.has(w.name)} busy={busy} onAdd={add} />)}</div>}
    </div>
  );
};
